// tiles-svg.ts — Vite-only: resolves the vendored amenoasa SVGs to bundled URLs and exposes
// `TILE_SVG: Record<TileId, string>` + `tileSvgUrl(id)`. This module uses `import … from '*.svg?url'`,
// which only the Studio's Vite build understands — it is intentionally EXCLUDED from the tsup `dist`
// build and from the plain-Node test runtime (see package.json `./tiles` export → this source file,
// and tsup.config.ts entry list). Keep all `?url` imports here so `tiles.ts` stays Node-safe.
import type { TileId } from '@mj/protocol';

// --- vendored faces (CC BY 4.0, amenoasa; back.svg is a generated placeholder). ---
import m1 from '../vendor/amenoasa/tiles/Man1.svg?url';
import m2 from '../vendor/amenoasa/tiles/Man2.svg?url';
import m3 from '../vendor/amenoasa/tiles/Man3.svg?url';
import m4 from '../vendor/amenoasa/tiles/Man4.svg?url';
import m5 from '../vendor/amenoasa/tiles/Man5.svg?url';
import m6 from '../vendor/amenoasa/tiles/Man6.svg?url';
import m7 from '../vendor/amenoasa/tiles/Man7.svg?url';
import m8 from '../vendor/amenoasa/tiles/Man8.svg?url';
import m9 from '../vendor/amenoasa/tiles/Man9.svg?url';
import m5r from '../vendor/amenoasa/tiles/Man5-Dora.svg?url';
import p1 from '../vendor/amenoasa/tiles/Pin1.svg?url';
import p2 from '../vendor/amenoasa/tiles/Pin2.svg?url';
import p3 from '../vendor/amenoasa/tiles/Pin3.svg?url';
import p4 from '../vendor/amenoasa/tiles/Pin4.svg?url';
import p5 from '../vendor/amenoasa/tiles/Pin5.svg?url';
import p6 from '../vendor/amenoasa/tiles/Pin6.svg?url';
import p7 from '../vendor/amenoasa/tiles/Pin7.svg?url';
import p8 from '../vendor/amenoasa/tiles/Pin8.svg?url';
import p9 from '../vendor/amenoasa/tiles/Pin9.svg?url';
import p5r from '../vendor/amenoasa/tiles/Pin5-Dora.svg?url';
import s1 from '../vendor/amenoasa/tiles/Sou1.svg?url';
import s2 from '../vendor/amenoasa/tiles/Sou2.svg?url';
import s3 from '../vendor/amenoasa/tiles/Sou3.svg?url';
import s4 from '../vendor/amenoasa/tiles/Sou4.svg?url';
import s5 from '../vendor/amenoasa/tiles/Sou5.svg?url';
import s6 from '../vendor/amenoasa/tiles/Sou6.svg?url';
import s7 from '../vendor/amenoasa/tiles/Sou7.svg?url';
import s8 from '../vendor/amenoasa/tiles/Sou8.svg?url';
import s9 from '../vendor/amenoasa/tiles/Sou9.svg?url';
import s5r from '../vendor/amenoasa/tiles/Sou5-Dora.svg?url';
import z1 from '../vendor/amenoasa/tiles/Ton.svg?url';
import z2 from '../vendor/amenoasa/tiles/Nan.svg?url';
import z3 from '../vendor/amenoasa/tiles/Shaa.svg?url';
import z4 from '../vendor/amenoasa/tiles/Pei.svg?url';
import z5 from '../vendor/amenoasa/tiles/Haku.svg?url';
import z6 from '../vendor/amenoasa/tiles/Hatsu.svg?url';
import z7 from '../vendor/amenoasa/tiles/Chun.svg?url';
import back from '../vendor/amenoasa/tiles/back.svg?url';
// 花牌 (flower/season) faces — owner-provided original art (春夏秋冬 / 梅蘭菊竹), sliced from the
// owner's hanahai3 sheet and keyed to face-only PNG so they sit on the same cream tile body as the
// amenoasa faces. f1-f4 = 春夏秋冬 (seasons), f5-f8 = 梅蘭菊竹 (flowers).
import f1 from '../vendor/flowers/f1.png?url';
import f2 from '../vendor/flowers/f2.png?url';
import f3 from '../vendor/flowers/f3.png?url';
import f4 from '../vendor/flowers/f4.png?url';
import f5 from '../vendor/flowers/f5.png?url';
import f6 from '../vendor/flowers/f6.png?url';
import f7 from '../vendor/flowers/f7.png?url';
import f8 from '../vendor/flowers/f8.png?url';

/**
 * TileId (+ `back`) → bundled SVG URL. Keyed by the SAME ids as `TILE_FILE`; ids absent from
 * `TILE_FILE` (z8/z9) are absent here too. `tileSvgUrl` returns `null` for those.
 */
export const TILE_SVG: Readonly<Record<string, string>> = {
  m1, m2, m3, m4, m5, m6, m7, m8, m9, m5r,
  p1, p2, p3, p4, p5, p6, p7, p8, p9, p5r,
  s1, s2, s3, s4, s5, s6, s7, s8, s9, s5r,
  z1, z2, z3, z4, z5, z6, z7,
  f1, f2, f3, f4, f5, f6, f7, f8,
  back,
};

/** Resolve a tile's bundled SVG URL, or `null` when no vendored face exists (z8/z9). */
export function tileSvgUrl(id: TileId | 'back'): string | null {
  return TILE_SVG[id] ?? null;
}

// Re-export the pure map so consumers can `import { TILE_SVG, TILE_FILE } from '@mj/assets/tiles'`.
export { TILE_FILE, TILES_WITH_ART, tileFileName } from './tiles.js';
